import { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Calendar, ClipboardList, FileText, Stethoscope, Users } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import AgendaPage from '../admin/AgendaPage';
import NewAssessmentsV2Page from '../admin/NewAssessmentsV2Page';
import PatientRegistrationPage from '../admin/PatientRegistrationPage';
import ProntuarioPage from '../admin/ProntuarioPage';
import { NotificationBell } from '../../components/common/NotificationBell';
import { AppointmentStatusModal } from '../../components/common/AppointmentStatusModal';
import { PendingAppointment } from '../../services/AppointmentStatusService';
import { buildTenantPath } from '../../utils/tenantSlug';

const FisioterapeutaDashboard = () => {
  const { user, selectedClient } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const { tenantSlug } = useParams<{ tenantSlug: string }>();
  const [activeTab, setActiveTab] = useState('cadastro');
  const [selectedAppointment, setSelectedAppointment] = useState<PendingAppointment | null>(null);
  const patientTabs = [
    { id: 'cadastro', label: 'Cadastro de Pacientes', icon: Users },
    { id: 'prontuario', label: 'Prontuário', icon: FileText },
    { id: 'avaliacoes', label: 'Avaliações Clínicas', icon: ClipboardList },
    { id: 'agenda', label: 'Agenda', icon: Calendar },
  ] as const;

  useEffect(() => {
    const pathSegments = location.pathname.split('/').filter(Boolean);
    const routeSegments = tenantSlug ? pathSegments.slice(1) : pathSegments;
    const currentSection = routeSegments[1];

    switch (currentSection) {
      case 'prontuario':
        setActiveTab('prontuario');
        break;
      case 'avaliacoes':
        setActiveTab('avaliacoes');
        break;
      case 'agenda':
        setActiveTab('agenda');
        break;
      default:
        setActiveTab('cadastro');
        break;
    }
  }, [location.pathname, tenantSlug]);

  const handleTabChange = (tab: string) => {
    setActiveTab(tab);

    const targetSegment = tab === 'cadastro' ? 'dashboard' : tab;
    navigate(
      buildTenantPath(`/fisioterapeuta/${targetSegment}`, selectedClient?.clientSlug || tenantSlug),
      { replace: true }
    );
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 'cadastro':
        return (
          <PatientRegistrationPage
            routePrefix="fisioterapeuta"
            permissions={{
              canCreatePatient: true,
              canEditPatient: false,
              canArchivePatient: false,
              canRestorePatient: false,
              canExportPatients: false,
              canViewArchivedPatients: false,
              canOpenFinancial: false,
            }}
          />
        );

      case 'prontuario':
        return <ProntuarioPage />;

      case 'avaliacoes':
        return <NewAssessmentsV2Page />;

      case 'agenda':
        return <AgendaPage />;

      default:
        return null;
    }
  };

  return (
    <div className="revahub-app min-h-screen bg-gray-100">
      <div className="border-b border-slate-200 bg-white py-5">
        <div className="container mx-auto flex items-center justify-between gap-4 px-4">
          <h1 className="text-xl font-bold text-slate-900 sm:text-2xl">Painel do Fisioterapeuta</h1>
          {user?.userDisplayName && (
            <div className="flex items-center gap-4">
              <span className="hidden text-sm text-slate-600 sm:inline">Olá, {user.userDisplayName}</span>
              {user.userId && selectedClient?.clientId && (
                <NotificationBell
                  clientId={selectedClient.clientId}
                  professionalName={user.userDisplayName}
                  userRole={selectedClient.userRole}
                  userId={user.userId}
                  onResolveAppointment={(appointment) => setSelectedAppointment(appointment)}
                />
              )}
            </div>
          )}
        </div>
      </div>

      <div className="container mx-auto px-4 py-8">
        <div className="mb-8">
          <div className="bg-white rounded-lg shadow-sm border mb-4">
            <div className="flex overflow-x-auto">
              <button
                className="flex items-center px-6 py-4 border-b-2 font-medium text-sm whitespace-nowrap transition-colors border-primary-500 text-primary-600 bg-primary-50"
              >
                <Stethoscope className="w-4 h-4 mr-2" />
                Pacientes
              </button>
            </div>
          </div>

          <div className="bg-gray-50 rounded-lg p-2">
            <div className="flex space-x-1 overflow-x-auto">
              {patientTabs.map((tab) => {
                const TabIcon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => handleTabChange(tab.id)}
                    className={`flex items-center px-4 py-2 rounded-md font-medium text-sm whitespace-nowrap transition-colors ${
                      activeTab === tab.id
                        ? 'bg-white text-primary-600 shadow-sm'
                        : 'text-gray-600 hover:text-gray-900 hover:bg-white/50'
                    }`}
                  >
                    <TabIcon className="w-4 h-4 mr-2" />
                    {tab.label}
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {renderTabContent()}
      </div>

      {selectedAppointment && user && (
        <AppointmentStatusModal
          appointment={selectedAppointment}
          userId={user.userId}
          onClose={() => setSelectedAppointment(null)}
          onResolved={() => {
            setSelectedAppointment(null);
          }}
        />
      )}
    </div>
  );
};

export default FisioterapeutaDashboard;
